import {css, StyleSheet} from 'aphrodite';
import React, {RefObject, useMemo, useRef, useState} from 'react';

import * as analytics from 'analytics/Analytics';
import {FixedPlateModalStep} from 'constants/analytics-products/SamplesAnalyticsConstants';
import {STORABLE_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import ClearWellsDropdown from 'samples/plate-maps/fill-plate-modal/ClearWellsDropdown';
import FillPlateReagentSettingsPane from 'samples/plate-maps/fill-plate-modal/FillPlateReagentSettingsPane';
import {FillPlateModalReducerActionType} from 'samples/plate-maps/fill-plate-modal/machine/FillPlateModalConstants';
import {FillPlateModalReducerDispatch} from 'samples/plate-maps/fill-plate-modal/useFillPlateModalReducer';
import {PatternParametersInput} from 'samples/plate-maps/pattern-controls/AssignWellsByPattern';
import {
  ENABLE_REMOVE_EXISTING_WELL_CONTENTS,
  PlateMap,
  PlateMapProcedureContext,
  PlateMapWellDisplayLayer,
  PlateModalAnalyticsMetadata,
  ReagentAssignmentGroup,
  REAGENTS_ATTRIBUTE_SENTINEL,
  Source,
  SourceErrorsBySourceDilutionId,
  WELL_ROLE_ATTRIBUTE_SENTINEL,
  WellAssignmentMethod,
} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {PlateMapWellSelectionSection} from 'samples/plate-maps/PlateMapWellSelectionSection';
import PlateMapValidationBanners from 'samples/plate-maps/validation/PlateMapValidationBanners';
import {getInvalidIdsFromReagentErrors} from 'samples/plate-maps/validation/PlateMapValidationUtil';
import {
  PlateMapAnnotationError,
  ValidationErrorsByLayerAndLevel,
} from 'samples/plate-maps/validation/ValidationTypes';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_DARKER, GRAY_LIGHT, GRAY_LIGHTER} from 'ui-library/Colors';

export interface FillPlateReagentStepProps {
  /** A function to dispatch change events to update the state props. */
  dispatch: FillPlateModalReducerDispatch;
  /** The plate map representing the plate being filled */
  initialPlateMap: PlateMap;
  /** How the wells will be filled by pattern */
  patternParams: PatternParametersInput;
  /** A preview of the plate as it is being filled, with preview reagents filled in to selected wells */
  previewPlateMap: PlateMap;
  /** What sources are currently being previewed, by source dilution id. Note that this is NOT the base
   * source id. */
  previewedSourcesBySourceDilutionId: {[sourceDilutionId: string]: Source};
  /** The ordering of previewed sources, i.e. for display under the fill controls */
  previewedSourceDilutionIdOrdering: Array<string>;
  /** Error messages for previewed sources to render in the reagent settings pane. */
  previewedSourceErrorsBySourceDilutionId: SourceErrorsBySourceDilutionId;
  /** Groupings of reagents being added together, e.g. when filling by pattern */
  reagentGroupsById: {[id: string]: ReagentAssignmentGroup};
  /** What indices in the plate are currently selected */
  selectedWellIndices: Set<number>;
  /** Whether wells are being filled by group or by pattern */
  wellAssignmentMethod: WellAssignmentMethod;
  /** Whether the pattern that sources are assigned by is based on role groups */
  assignByRoleGroups: boolean;
  /** Whether the current well selection allows assigning sources by role group */
  canAssignByRoleGroups: boolean;
  /** Ref for the modal displaying this step */
  modalRef?: RefObject<HTMLDivElement>;
  /** Analytics metadata included with any analytics events */
  analyticsMetadata: PlateModalAnalyticsMetadata;
  /** Max height of the plate view in this step, determined by the parent modal */
  maxPlateViewHeight: string;
  /** Whether the well role preview should be shown initially */
  initialShouldPreviewWellRoles: boolean;
  plateMapType: PlateMapType;
  /** Context relevant if in a procedure, including material inputs to enable adding planned values. */
  procedureContext: PlateMapProcedureContext;
  /** Plate map validation errors grouped by error level. */
  groupedValidationErrors?: ValidationErrorsByLayerAndLevel;
  /** A function to be called when an error banner is dismissed. */
  onDismissErrors: (errorUuids: Set<string>) => void;
}

/**
 * Renders settings and preview for selecting source reagents to transfer into wells.
 * @constructor
 */
export default function FillPlateReagentStep({
  dispatch,
  initialPlateMap,
  patternParams,
  previewPlateMap,
  previewedSourcesBySourceDilutionId,
  previewedSourceDilutionIdOrdering,
  previewedSourceErrorsBySourceDilutionId,
  reagentGroupsById,
  selectedWellIndices,
  wellAssignmentMethod,
  assignByRoleGroups,
  canAssignByRoleGroups,
  modalRef,
  analyticsMetadata,
  maxPlateViewHeight,
  initialShouldPreviewWellRoles,
  procedureContext,
  plateMapType,
  groupedValidationErrors,
  onDismissErrors,
}: FillPlateReagentStepProps): JSX.Element {
  const mapPaneWrapperRef = useRef<HTMLDivElement>(null);
  const mapHeaderRef = useRef<HTMLDivElement>(null);
  const selectedWellsParagraphRef = useRef<HTMLParagraphElement>(null);
  const [previewWellRoles, setPreviewWellRoles] = useState<boolean>(initialShouldPreviewWellRoles);

  const shouldDisableWellRolePreview = useMemo(
    () => initialPlateMap.wells.every((well) => well.role == null),
    [initialPlateMap]
  );

  const displayLayer = previewWellRoles
    ? PlateMapWellDisplayLayer.WELL_ROLE
    : PlateMapWellDisplayLayer.REAGENT;

  const displayAttribute = previewWellRoles ? WELL_ROLE_ATTRIBUTE_SENTINEL : REAGENTS_ATTRIBUTE_SENTINEL;

  function trackEventWithStep(event: analytics.AnalyticsEvent): void {
    analytics.trackEvent(event, {
      ...analyticsMetadata,
      step: FixedPlateModalStep.TRANSFER_SOURCES_STEP,
    });
  }

  function onResolveErrorBanner(errors: Array<PlateMapAnnotationError>): void {
    const dedupedSourceIds = getInvalidIdsFromReagentErrors(errors);
    dispatch({
      type: FillPlateModalReducerActionType.REMOVE_SOURCES,
      sourceIds: dedupedSourceIds,
    });
  }
  const reagentErrors = groupedValidationErrors?.[REAGENTS_ATTRIBUTE_SENTINEL];

  function setSelectedWellIndices(selectedWells: Set<number>): void {
    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CHANGED_CELL_SELECTION);
    dispatch({type: FillPlateModalReducerActionType.SET_SELECTED_WELLS, selectedWells});
  }

  return (
    <div
      className={css(styles.wrapper)}
      {...getTestProps({component: 'FillPlateReagentStep', element: 'root'})}
    >
      <div className={css(styles.mapPaneWrapper)} ref={mapPaneWrapperRef}>
        <div className={css(styles.errorBanners)}>
          {reagentErrors && (
            <PlateMapValidationBanners
              errorsByLevel={reagentErrors}
              onResolveError={onResolveErrorBanner}
              onDismissErrors={onDismissErrors}
            />
          )}
        </div>
        <PlateMapPane
          header={
            <div className={css(styles.mapHeader)} ref={mapHeaderRef}>
              <PlateMapWellSelectionSection
                selectedWellIndices={selectedWellIndices}
                paragraphRef={selectedWellsParagraphRef}
                setSelectedWellIndices={setSelectedWellIndices}
                totalWells={previewPlateMap.wells.length}
              />
              <div>
                <Button
                  className={css(styles.previewWellRolesButton)}
                  disabled={shouldDisableWellRolePreview}
                  buttonStyle={
                    previewWellRoles ? ButtonStyle.TERTIARY_BLUE_POWDER : ButtonStyle.TERTIARY_GRAY
                  }
                  size={ButtonSize.SMALL}
                  onClick={() => {
                    setPreviewWellRoles(!previewWellRoles);
                  }}
                  text={`Preview well roles: ${previewWellRoles ? 'On' : 'Off'}`}
                  icon={ModelTypeConstants.TEMPLATE_COLLECTION}
                />
                <ClearWellsDropdown
                  selectedWellIndices={selectedWellIndices}
                  // This dropdown only cares about what's actually in the plate map, not the preview state
                  plateMap={initialPlateMap}
                  reagentGroupsById={reagentGroupsById}
                  enableRemoveExistingWellContents={ENABLE_REMOVE_EXISTING_WELL_CONTENTS}
                  removeContentGroups={(contentGroupIds, clearAllWells) => {
                    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);
                    dispatch({
                      type: FillPlateModalReducerActionType.REMOVE_CONTENT_GROUPS,
                      contentGroupIds,
                      clearAllWells,
                    });
                  }}
                  removeReagentGroups={(reagentGroupIds, clearAllWells) => {
                    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);
                    dispatch({
                      type: FillPlateModalReducerActionType.REMOVE_APPLIED_SOURCE_GROUPS,
                      reagentGroupIds,
                      clearAllWells,
                    });
                  }}
                  removeAllReagentGroups={(clearAllWells) => {
                    trackEventWithStep(STORABLE_ANALYTICS_ACTIONS.CLICKED_CLEAR_CELLS);
                    dispatch({
                      type: FillPlateModalReducerActionType.REMOVE_ALL_APPLIED_SOURCE_GROUPS,
                      clearAllWells,
                    });
                  }}
                />
              </div>
            </div>
          }
          plateMapDisplayParams={{
            plateMap: previewPlateMap,
            displayLayer,
            attribute: displayAttribute,
            assignmentGroupsById: reagentGroupsById,
            groupedValidationErrors: groupedValidationErrors ?? {},
          }}
          isPlateMapViewOnly={false}
          selectedWellIndices={selectedWellIndices}
          setSelectedWellIndices={setSelectedWellIndices}
          previewedWellIndices={
            previewedSourceDilutionIdOrdering.length > 0 ? selectedWellIndices : new Set<number>()
          }
          deselectWellsOnClickRefs={[mapPaneWrapperRef, mapHeaderRef, selectedWellsParagraphRef]}
          modalRef={modalRef}
          maxPlateViewHeight={maxPlateViewHeight}
          showFinalMeasurements={true}
          plateMapType={plateMapType}
          previewedSourceErrorsBySourceDilutionId={previewedSourceErrorsBySourceDilutionId}
        />
      </div>
      <div className={css(styles.border)} />
      <FillPlateReagentSettingsPane
        dispatch={dispatch}
        initialPlateMap={initialPlateMap}
        patternParams={patternParams}
        previewPlateMap={previewPlateMap}
        previewedSourcesBySourceDilutionId={previewedSourcesBySourceDilutionId}
        previewedSourceDilutionIdOrdering={previewedSourceDilutionIdOrdering}
        previewedSourceErrorsBySourceDilutionId={previewedSourceErrorsBySourceDilutionId}
        reagentGroupsById={reagentGroupsById}
        selectedWellIndices={selectedWellIndices}
        wellAssignmentMethod={wellAssignmentMethod}
        assignByRoleGroups={assignByRoleGroups}
        canAssignByRoleGroups={canAssignByRoleGroups}
        setPreviewWellRoles={setPreviewWellRoles}
        analyticsMetadata={analyticsMetadata}
        procedureContext={procedureContext}
        plateMapType={plateMapType}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flex: 1,
    maxHeight: 'calc(100vh - 280px)',
    overflow: 'auto',
    margin: '-16px',
    padding: '16px',
  },
  // This is a border between the plate view and the search view. We want it to reach all the way to the top
  // and bottom of the modal body, which has a 16px padding on all sides.
  border: {
    flex: '0 0 0px',
    borderRight: `1px solid ${GRAY_LIGHT}`,
    margin: '-16px 16px',
  },
  mapPaneWrapper: {
    flex: '1 1 auto',
    overflow: 'auto',
  },
  mapHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    textWrap: 'nowrap',
  },
  wellsSelectedText: {
    color: GRAY_DARKER,
    marginRight: '16px',
  },
  previewWellRolesButton: {
    marginRight: '8px',
  },
  sourcesSettingsPane: {
    marginLeft: 16,
  },
  reagentSourceView: {
    display: 'flex',
    flexDirection: 'column',
    width: '420px',
    minWidth: '420px',
  },
  searchHeaderText: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: '16px',
  },
  wellAssignmentMethodSelectorWrapper: {
    margin: '16px 0px',
  },
  useRoleCheckbox: {
    marginBottom: '16px',
  },
  patternControlsWrapper: {
    marginBottom: '16px',
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 2fr',
    gridGap: '16px',
  },
  reagentListWrapper: {
    backgroundColor: GRAY_LIGHTER,
    paddingTop: '16px',
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  reagentListWrapperHeader: {
    marginBottom: '16px',
    padding: '0px 16px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bannerWrapper: {
    marginBottom: '16px',
  },
  reagentList: {
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  scroll: {
    padding: '0px 16px 16px 16px',
    overflow: 'auto',
    height: '100%',
  },
  reagentCount: {
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
  },
  reagentChipWrapper: {
    ':not(:last-child)': {
      marginBottom: '8px',
    },
    display: 'flex',
    alignItems: 'center',
  },
  addReagentButtonWrapper: {
    marginTop: '16px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  errorBanners: {
    marginBottom: '16px',
  },
});
